import { useRef } from 'react'
import type { ReactNode, RefObject } from 'react'

import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useIsWideViewport } from '@/hooks/useIsWideViewport'

interface ConversationSidebarPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  triggerRef: RefObject<HTMLButtonElement>
  /**
   * Drawer only: runs once the drawer has closed. Return true when the caller moves focus
   * itself (e.g. the profile card's Ask focuses the composer), so the trigger does not take it.
   */
  onClosedFocus?: () => boolean
  children: ReactNode
}

export function ConversationSidebarPanel(props: ConversationSidebarPanelProps) {
  const { open, onOpenChange, triggerRef, onClosedFocus, children } = props
  const isWide = useIsWideViewport()
  const drawerRef = useRef<HTMLDivElement>(null)

  // Mount just one copy: hidden desktop controls must not remain tabbable,
  // and the mobile modal must release its focus trap when the viewport grows.
  if (isWide) {
    return open ? (
      <div data-testid="conversation-sidebar-desktop" className="flex min-h-0 w-[405px] shrink-0 flex-col pb-5 pl-2 pr-0 pt-0">
        {children}
      </div>
    ) : null
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        data-testid="conversation-sidebar-drawer"
        className="flex w-[min(405px,100vw)] max-w-full flex-col overflow-hidden p-4 pt-12 sm:max-w-[405px]"
        aria-describedby={undefined}
        ref={drawerRef}
        onOpenAutoFocus={(event) => {
          // Land on the drawer itself, not its first control: the first control
          // is a twin circle whose tooltip would open over the panel on focus.
          event.preventDefault()
          drawerRef.current?.focus()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (onClosedFocus?.()) {
            return
          }
          triggerRef.current?.focus()
        }}
      >
        <SheetTitle className="sr-only">Conversation details</SheetTitle>
        {children}
      </SheetContent>
    </Sheet>
  )
}
